// =============================================
// Name search that forgives a slip of the keyboard
// =============================================
// What the search box offers while typing, in this order:
//   starts   — names that START with what was typed
//   contains — names (or their other names) that contain it
//   similar  — "did you mean": names a typing slip away — a wrong letter (Clee → Clea…),
//              a missing or extra one, or two swapped (Micheal → Michael)
//
// Pure + unit-tested (nameSearch.test.ts).
// =============================================

const norm = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Edit distance counting a swap of two neighbouring letters as ONE slip (Damerau). */
export function editDistance(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev2: number[] = [];
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let d = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, prev2[j - 2] + 1);
      cur.push(d);
    }
    prev2 = prev;
    prev = cur;
  }
  return prev[n];
}

/** Slips forgiven for a typed text of this length: none under 3 letters, 1 up to 6, then 2. */
export const slipsAllowed = (len: number) => (len < 3 ? 0 : len <= 6 ? 1 : 2);

/**
 * How far `term` is from the START of any word (or of the whole text) of `text` — the
 * user is still typing, so "clee" is compared with "clea" of "clearview", not the whole
 * word. Infinity when further than the slips allowed.
 */
export function slipDistance(term: string, text: string): number {
  const t = norm(term);
  const full = norm(text);
  const allowed = slipsAllowed(t.length);
  if (!allowed || !full) return Infinity;
  let best = Infinity;
  const starts = [full, ...full.split(' ')];
  for (const w of starts) {
    // a slip can add or drop a letter, so try the neighbouring lengths too
    for (let len = Math.max(1, t.length - 1); len <= Math.min(w.length, t.length + 1); len++) {
      // the first letter is rarely the one mistyped — requiring it keeps noise out
      if (w[0] !== t[0]) break;
      best = Math.min(best, editDistance(t, w.slice(0, len)));
    }
  }
  return best <= allowed ? best : Infinity;
}

export interface NameCandidate<T> {
  item: T;
  name: string;            // the main name
  others?: string[];       // legal name, nickname, aliases …
}

export interface RankedNames<T> { starts: T[]; contains: T[]; similar: T[] }

/** Split candidates into starts-with / contains / a-slip-away, each by name. */
export function rankNames<T>(term: string, candidates: NameCandidate<T>[]): RankedNames<T> {
  const t = norm(term);
  const out: RankedNames<T> = { starts: [], contains: [], similar: [] };
  if (!t) return out;
  const near: { item: T; d: number; name: string }[] = [];
  [...candidates].sort((a, b) => a.name.localeCompare(b.name)).forEach(c => {
    const name = norm(c.name);
    const all = [name, ...(c.others || []).map(norm)].filter(Boolean);
    if (name.startsWith(t)) out.starts.push(c.item);
    else if (all.some(v => v.includes(t))) out.contains.push(c.item);
    else {
      const d = Math.min(...all.map(v => slipDistance(t, v)));
      if (d !== Infinity) near.push({ item: c.item, d, name: c.name });
    }
  });
  out.similar = near.sort((a, b) => a.d - b.d || a.name.localeCompare(b.name)).map(x => x.item);
  return out;
}
